import React from "react";

import {
  PLAYHEAD_BASE_OFFSET_PX,
} from "../lib/playbackViewportLayout";

/** @deprecated usar PLAYHEAD_BASE_OFFSET_PX desde playbackViewportLayout */
export const PLAYHEAD_LEFT_OFFSET_PX = PLAYHEAD_BASE_OFFSET_PX;

export interface PlaybackPlayheadOverlayProps {
  /** Columna horizontal del needle (px), de la izquierda del padre `relative`. */
  leftOffsetPx?: number;
  /** Color principal de la línea (hex). */
  accent: string;
  /** Sombre / resplandor alrededor de la línea (rgba u otro). */
  accentGlow: string;
  /** Ancho máximo del velo difuminado “pasado” a la izquierda de la aguja. */
  trailMaxPx?: number;
  /** Recorte vertical: inset superior (px). Si se omite, ocupa todo el padre. */
  topInsetPx?: number;
  /** Recorte vertical: inset inferior (px). */
  bottomInsetPx?: number;
  /** Grosor de la aguja (px). */
  needleWidthPx?: number;
  className?: string;
}

/**
 * Playhead fijo en el viewport: velo suave de lo ya pasado + aguja con brillo y marca superior.
 */
export function PlaybackPlayheadOverlay({
  accent,
  accentGlow,
  trailMaxPx = 260,
  className = "",
  leftOffsetPx = PLAYHEAD_BASE_OFFSET_PX,
  topInsetPx,
  bottomInsetPx,
  needleWidthPx = 2,
}: PlaybackPlayheadOverlayProps) {
  const vertical =
    topInsetPx != null && bottomInsetPx != null
      ? { top: topInsetPx, bottom: bottomInsetPx }
      : { top: 0, bottom: 0 };

  const halfNeedle = needleWidthPx / 2;

  const trail = (
    <div
      className="absolute pointer-events-none"
      style={{
        ...vertical,
        right: 0,
        width: `min(${trailMaxPx}px, 42vw)`,
        background: `linear-gradient(90deg, 
          transparent 0%, 
          ${withAlpha(accent, 0.04)} 22%, 
          ${withAlpha(accent, 0.1)} 52%, 
          ${withAlpha(accent, 0.2)} 88%, 
          ${withAlpha(accent, 0.28)} 100%)`,
        maskImage: "linear-gradient(90deg, transparent 0%, black 32%, black 100%)",
        WebkitMaskImage: "linear-gradient(90deg, transparent 0%, black 32%, black 100%)",
      }}
    />
  );

  const softBloom = (
    <div
      className="absolute pointer-events-none"
      style={{
        ...vertical,
        right: -2,
        width: 16,
        background: `linear-gradient(90deg, transparent 0%, ${withAlpha(accent, 0.12)} 55%, transparent 100%)`,
        filter: "blur(6px)",
        opacity: 0.75,
      }}
    />
  );

  const needle = (
    <div
      className="absolute pointer-events-none"
      style={{
        ...vertical,
        left: -halfNeedle,
        width: needleWidthPx,
        borderRadius: needleWidthPx,
        background: `linear-gradient(180deg, 
          transparent 0%, 
          ${withAlpha(accent, 0.2)} 6%, 
          ${accent} 16%, 
          ${accent} 84%, 
          ${withAlpha(accent, 0.2)} 94%, 
          transparent 100%)`,
        boxShadow: `
          0 0 1px rgba(255,255,255,0.35),
          0 0 8px ${accentGlow},
          0 0 16px ${withAlpha(accent, 0.28)}
        `,
      }}
    />
  );

  const capTop =
    topInsetPx != null ? topInsetPx + 2 : 4;

  const cap = (
    <div
      className="absolute pointer-events-none z-10"
      style={{
        top: capTop,
        left: -4.5,
        width: 9,
        height: 9,
        transform: "rotate(45deg)",
        background: `linear-gradient(135deg, rgba(255,255,255,0.5) 0%, ${accent} 40%, ${withAlpha(
          accent,
          0.9
        )} 100%)`,
        borderRadius: 2,
        boxShadow: `0 0 8px ${accentGlow}, 0 1px 4px rgba(0,0,0,0.3)`,
      }}
    />
  );

  return (
    <div
      aria-hidden
      className={`pointer-events-none absolute z-20 ${className}`}
      style={{
        left: leftOffsetPx,
        width: 0,
        ...vertical,
      }}
    >
      {trail}
      {softBloom}
      {needle}
      {cap}
    </div>
  );
}

function withAlpha(hex: string, a: number): string {
  const { r, g, b } = parseHexRgb(hex);
  return `rgba(${r},${g},${b},${a})`;
}

function parseHexRgb(hex: string): { r: number; g: number; b: number } {
  const h = hex.replace("#", "").trim();
  if (h.length === 3) {
    return {
      r: parseInt(h[0] + h[0], 16),
      g: parseInt(h[1] + h[1], 16),
      b: parseInt(h[2] + h[2], 16),
    };
  }
  if (h.length >= 6) {
    return {
      r: parseInt(h.slice(0, 2), 16),
      g: parseInt(h.slice(2, 4), 16),
      b: parseInt(h.slice(4, 6), 16),
    };
  }
  return { r: 201, g: 64, b: 90 };
}
